import { useEditorStore } from '@/stores/editor/editor-store';
import type { AgentVisualResponse, CanvasAction } from '@/services/api/agentApi';
import { ComponentType, type Component, type ComponentInput, type ComponentUpdateWithBreakpoint, PageTemplate } from '@/types';
import { isColorSchemeWriteTarget } from '@/types/color-scheme';
import {
  isComponentDataBinding,
  parseComponentDataBinding,
  parseComponentDataBindingWriteTarget,
  type ComponentDataBinding,
  type ComponentDataBindingWriteTarget,
  type ComponentDataWriteOperation,
  type DataBindingFieldMapping,
} from '@/types/componentDataBinding';
import { applySchemaAwareStyleUpdate } from '@/utils/component-style-normalization';
import { PostActionTracker } from './PostActionTracker';
import { logger } from '@/utils/logger';
import {
  createCurrentUserAICommandPermissionGuard,
  getAICommandMutationCategoryLabel,
  getMutationCategoriesForBatchComponentUpdate,
  getMutationCategoriesForCanvasAction,
  type AICommandMutationCategory,
} from '@/services/ai/agentCommandPermissions';
import {
  cloneSanitizedRecord,
  copySanitizedOwnEnumerableProperties,
  isPlainRecord,
  mergeSanitizedRecords,
  omitSanitizedRecordKeys,
} from '@/utils/safeRecordSanitization';

export interface ApplyResult {
  applied: string[];
  failed: string[];
  totalActions: number;
  diff?: SnapshotDiff;
}

// --- Visual diff types ---

export interface ComponentSnapshot {
  type: string;
  name: string;
  parentId: string;
  style: Record<string, unknown>;
  properties: Record<string, unknown>;
  size: { width: number; height: number };
}

export interface SnapshotDiff {
  added: string[];
  removed: string[];
  modified: Array<{
    componentId: string;
    componentName: string;
    componentType: string;
    changes: string[];
  }>;
  summary: string;
}

// --- Sibling style cloning diagnostics ---

export interface CloningDetail {
  componentId: string;
  componentType: string;
  donorId: string | null;
  donorType: string | null;
  isSameType: boolean;
  isCousinMatch: boolean;
  cousinParentId: string | null;
  siblingCount: number;
  clonedStyle: string[];
  clonedProperties: string[];
  clonedBreakpointStyles: Record<string, string[]>;
}

let _lastCloningDetails: CloningDetail[] = [];

export function getAndClearCloningDetails(): CloningDetail[] {
  const details = _lastCloningDetails;
  _lastCloningDetails = [];
  return details;
}

function copySafeOwnEnumerableProperties(
  target: Record<string, unknown>,
  source: unknown,
): void {
  copySanitizedOwnEnumerableProperties(target, source);
}

function cloneSafeOwnEnumerableRecord(source: unknown): Record<string, unknown> {
  return cloneSanitizedRecord(source);
}

function mergeSafeOwnEnumerableRecords(...sources: unknown[]): Record<string, unknown> {
  return mergeSanitizedRecords(...sources);
}

function omitRecordKeys(source: Record<string, unknown>, keys: Iterable<string>): Record<string, unknown> {
  return omitSanitizedRecordKeys(source, keys);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isPlainRecord(value);
}

function getRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined;
}

function getObjectRecord(value: unknown): Record<string, unknown> | undefined {
  return getRecord(value);
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function getStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? value
    : undefined;
}

const AI_DATA_BINDING_TARGET_TYPES = new Set<ComponentType>([
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.AREA_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.MAP_CHART,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
  ComponentType.KANBAN_BOARD,
  ComponentType.CHECKLIST,
  ComponentType.HORIZONTAL_TIMELINE,
  ComponentType.ASSIGNMENT_WIDGET,
  ComponentType.GANTT_CHART,
  ComponentType.CALENDAR,
  ComponentType.PROJECT_HUB,
  ComponentType.QUESTIONNAIRE,
  ComponentType.VOTING_CARD,
  ComponentType.TIME_TRACKER,
]);

const AI_LEGACY_BINDING_PROPERTY_KEYS = new Set([
  'dataSource',
  'queryTemplateId',
  'queryDefinition',
  'compositeQueryDefinition',
]);

const AI_RAW_RECORD_PROPERTY_KEYS = new Set([
  'data',
  'records',
  'rows',
  'items',
  'sampleData',
  'tasks',
  'cards',
  'events',
  'projects',
  'clients',
]);

function hasRecordItemArray(value: unknown): boolean {
  return Array.isArray(value) && value.some((item) => getRecord(item) !== undefined);
}

function hasRecordArrayPayload(value: unknown): boolean {
  if (hasRecordItemArray(value)) {
    return true;
  }

  const record = getRecord(value);
  if (!record) {
    return false;
  }

  return ['data', 'records', 'rows', 'items'].some((key) => hasRecordItemArray(record[key]));
}

function hasRawRecordArrayProperty(record: Record<string, unknown>): boolean {
  return Object.values(record).some(hasRecordArrayPayload);
}

function hasKnownRawRecordArrayProperty(record: Record<string, unknown>): boolean {
  for (const [key, value] of Object.entries(record)) {
    if (AI_RAW_RECORD_PROPERTY_KEYS.has(key) && hasRecordArrayPayload(value)) {
      return true;
    }
  }

  return false;
}

function hasLegacyBindingProperty(record: Record<string, unknown>): boolean {
  for (const key of AI_LEGACY_BINDING_PROPERTY_KEYS) {
    if (Object.hasOwn(record, key)) {
      return true;
    }
  }

  return false;
}

function assertAIDataBindingTargetComponent(component: Pick<Component, 'type'> | undefined): void {
  if (!component) {
    throw new Error('dataBinding target component not found');
  }

  if (!AI_DATA_BINDING_TARGET_TYPES.has(component.type)) {
    throw new Error('dataBinding updates are limited to chart, table, and PM components during Phase 11B');
  }
}

function canonicalizeAIDataBinding(dataBinding: unknown): ComponentDataBinding {
  const dataBindingRecord = getRecord(dataBinding);
  if (dataBindingRecord && hasRawRecordArrayProperty(dataBindingRecord)) {
    throw new Error('dataBinding updates cannot include copied record arrays');
  }

  const sourceRecord = getRecord(dataBindingRecord?.source);
  if (sourceRecord && hasRawRecordArrayProperty(sourceRecord)) {
    throw new Error('dataBinding updates cannot include copied record arrays');
  }

  if (!isComponentDataBinding(dataBinding)) {
    throw new Error('dataBinding must include an explicit schema, query-template, or federated-view source');
  }

  if (dataBinding.writeTarget !== undefined) {
    throw new Error('dataBinding writeTarget updates are deferred to Phase 11C');
  }

  const fieldMapping: DataBindingFieldMapping = {};
  for (const [key, value] of Object.entries(dataBinding.fieldMapping)) {
    fieldMapping[key] = value;
  }

  if (dataBinding.source.kind === 'schema') {
    return {
      source: {
        kind: 'schema',
        dataOwnerOrganizationId: dataBinding.source.dataOwnerOrganizationId,
        ownerType: dataBinding.source.ownerType,
        ownerId: dataBinding.source.ownerId,
        groupId: dataBinding.source.groupId,
        schemaId: dataBinding.source.schemaId,
      },
      fieldMapping,
    };
  }

  if (dataBinding.source.kind === 'query-template') {
    return {
      source: {
        kind: 'query-template',
        ownerType: dataBinding.source.ownerType,
        ownerId: dataBinding.source.ownerId,
        viewOrganizationId: dataBinding.source.viewOrganizationId,
        viewId: dataBinding.source.viewId,
      },
      fieldMapping,
    };
  }

  if (dataBinding.source.kind === 'federated-view') {
    return {
      source: {
        kind: 'federated-view',
        ownerType: dataBinding.source.ownerType,
        ownerId: dataBinding.source.ownerId,
        viewOrganizationId: dataBinding.source.viewOrganizationId,
        viewId: dataBinding.source.viewId,
      },
      fieldMapping,
    };
  }

  throw new Error('dataBinding source must be schema, query-template, or federated-view');
}

function normalizeAIDataBindingPropertiesUpdate(
  properties: unknown,
  targetComponent: Pick<Component, 'type'> | undefined,
): Record<string, unknown> | undefined {
  const propertiesRecord = getRecord(properties);
  if (!propertiesRecord) {
    return undefined;
  }

  if (!Object.hasOwn(propertiesRecord, 'dataBinding')) {
    if (hasLegacyBindingProperty(propertiesRecord)) {
      throw new Error('legacy AI data binding updates must use properties.dataBinding.source with full source or view identity');
    }

    if (hasKnownRawRecordArrayProperty(propertiesRecord)) {
      throw new Error('AI component updates cannot include copied record arrays');
    }

    return undefined;
  }

  assertAIDataBindingTargetComponent(targetComponent);

  if (hasRawRecordArrayProperty(propertiesRecord)) {
    throw new Error('dataBinding updates cannot include copied record arrays');
  }

  const normalizedProperties = cloneSafeOwnEnumerableRecord(propertiesRecord);
  normalizedProperties.dataBinding = canonicalizeAIDataBinding(propertiesRecord.dataBinding);
  return normalizedProperties;
}

const AI_PM_WRITE_OPERATIONS: ComponentDataWriteOperation[] = ['create', 'update', 'delete'];

const AI_PM_WRITE_TARGET_TYPES = new Set<ComponentType>([
  ComponentType.KANBAN_BOARD,
  ComponentType.CHECKLIST,
  ComponentType.HORIZONTAL_TIMELINE,
  ComponentType.ASSIGNMENT_WIDGET,
  ComponentType.GANTT_CHART,
  ComponentType.CALENDAR,
  ComponentType.PROJECT_HUB,
  ComponentType.QUESTIONNAIRE,
  ComponentType.VOTING_CARD,
  ComponentType.TIME_TRACKER,
]);

function isComponentDataWriteOperation(value: unknown): value is ComponentDataWriteOperation {
  return typeof value === 'string' && AI_PM_WRITE_OPERATIONS.some((operation) => operation === value);
}

function getAIWriteTargetComponentId(payload: Record<string, unknown>): string | undefined {
  return getComponentIdCandidate(payload.componentId) || getComponentIdCandidate(payload.id);
}

function isSameSchemaWriteTarget(
  left: { kind: 'schema'; dataOwnerOrganizationId: string; ownerType: string; ownerId: string; groupId: string; schemaId: string },
  right: { kind: 'schema'; dataOwnerOrganizationId: string; ownerType: string; ownerId: string; groupId: string; schemaId: string },
): boolean {
  return (
    left.dataOwnerOrganizationId === right.dataOwnerOrganizationId &&
    left.ownerType === right.ownerType &&
    left.ownerId === right.ownerId &&
    left.groupId === right.groupId &&
    left.schemaId === right.schemaId
  );
}

function isSameFederatedViewWriteTarget(
  left: ComponentDataBindingWriteTarget,
  right: ComponentDataBindingWriteTarget,
): boolean {
  if (left.kind !== 'federated-view-source' || right.kind !== 'federated-view-source') {
    return false;
  }

  return (
    left.targetSourceId === right.targetSourceId &&
    left.view.kind === right.view.kind &&
    left.view.ownerType === right.view.ownerType &&
    left.view.ownerId === right.view.ownerId &&
    left.view.viewOrganizationId === right.view.viewOrganizationId &&
    left.view.viewId === right.view.viewId &&
    isSameSchemaWriteTarget(left.source, right.source)
  );
}

function isSameComponentDataBindingWriteTarget(
  left: ComponentDataBindingWriteTarget,
  right: ComponentDataBindingWriteTarget,
): boolean {
  if (left.kind === 'federated-view-source' || right.kind === 'federated-view-source') {
    return isSameFederatedViewWriteTarget(left, right);
  }

  return isSameSchemaWriteTarget(left, right);
}

function resolveAIExplicitWriteTarget(payload: Record<string, unknown>): ComponentDataBindingWriteTarget {
  const targetValues: unknown[] = [];

  if (Object.hasOwn(payload, 'writeTarget')) {
    targetValues.push(payload.writeTarget);
  }

  for (const key of ['writeTargets', 'candidateWriteTargets', 'candidates']) {
    if (!Object.hasOwn(payload, key)) {
      continue;
    }

    const candidates = payload[key];
    if (!Array.isArray(candidates)) {
      throw new Error('AI write command writeTarget is malformed; clarification is required.');
    }

    targetValues.push(...candidates);
  }

  let resolvedTarget: ComponentDataBindingWriteTarget | undefined;
  for (const targetValue of targetValues) {
    const parsedTarget = parseComponentDataBindingWriteTarget(targetValue);
    if (parsedTarget === undefined) {
      throw new Error('AI write command writeTarget is malformed; clarification is required.');
    }

    if (resolvedTarget !== undefined && !isSameComponentDataBindingWriteTarget(resolvedTarget, parsedTarget)) {
      throw new Error('AI write command found multiple writable targets; clarification is required before writing.');
    }

    resolvedTarget = parsedTarget;
  }

  if (resolvedTarget === undefined) {
    throw new Error('AI write command requires exactly one explicit writable target; clarification is required.');
  }

  return resolvedTarget;
}

function getAIWriteTargetBindingMetadata(component: Component): Record<string, unknown> | undefined {
  return [
    component.metadata?.dataBinding,
    component.metadata?.binding,
    component.metadata?.bindingMetadata,
    component.metadata?.dataBindingMetadata,
    component.properties.bindingMetadata,
    component.properties.dataBindingMetadata,
  ]
    .map(getObjectRecord)
    .find((record) => record !== undefined);
}

function getComponentWriteReadinessState(
  component: Component,
  operation: ComponentDataWriteOperation,
): 'ready' | 'not-ready' | 'missing' {
  const metadata = getAIWriteTargetBindingMetadata(component);
  const readinessInput = [
    metadata?.writeReadiness,
    metadata?.operationReadiness,
    component.properties.writeReadiness,
    component.properties.operationReadiness,
  ]
    .map(getObjectRecord)
    .find((record) => record !== undefined);

  if (readinessInput === undefined) {
    return 'missing';
  }

  const value = readinessInput[operation];
  if (value === true || value === 'ready') {
    return 'ready';
  }

  if (
    value === false ||
    value === 'not-ready' ||
    value === 'missing' ||
    value === 'reconnect' ||
    value === 'unknown'
  ) {
    return 'not-ready';
  }

  return 'missing';
}

function validateAIWriteTargetPreflightAction(
  action: CanvasAction,
  store: ReturnType<typeof useEditorStore.getState>,
): void {
  if (action.type !== 'preflight_pm_write_target') {
    return;
  }

  const payload = getRecord(action.payload);
  if (!payload) {
    throw new Error('AI write command requires explicit write-target clarification.');
  }

  const operation = payload.operation;
  if (!isComponentDataWriteOperation(operation)) {
    throw new Error('AI write command requires create, update, or delete operation-specific readiness clarification.');
  }

  const actionWriteTarget = resolveAIExplicitWriteTarget(payload);

  const capabilities = getRecord(payload.capabilities);
  if (payload.permissionMode === 'read-only' || capabilities?.writable === false) {
    throw new Error('AI write command refused because the selected source is read-only.');
  }

  const componentId = getAIWriteTargetComponentId(payload);
  if (componentId === undefined) {
    throw new Error('AI write command requires an existing target componentId; clarification is required.');
  }

  const targetComponent = store.getComponent(componentId);
  if (targetComponent === undefined) {
    throw new Error('AI write command target component was not found; clarification is required.');
  }

  if (!AI_PM_WRITE_TARGET_TYPES.has(targetComponent.type)) {
    throw new Error('AI write command is limited to PM components with resolver-backed write targets.');
  }

  const targetBinding = parseComponentDataBinding(targetComponent.properties.dataBinding);
  const existingWriteTarget = targetBinding?.writeTarget;
  if (existingWriteTarget === undefined) {
    throw new Error('AI write command target component requires an existing explicit dataBinding.writeTarget; clarification is required.');
  }

  if (!isSameComponentDataBindingWriteTarget(actionWriteTarget, existingWriteTarget)) {
    throw new Error('AI write command writeTarget must exactly match the target component explicit writeTarget; clarification is required.');
  }

  const readiness = getComponentWriteReadinessState(targetComponent, operation);
  if (readiness === 'missing') {
    throw new Error(`AI write command ${operation} readiness is unclear; clarification is required before writing.`);
  }

  if (readiness !== 'ready') {
    throw new Error(`AI write command ${operation} readiness is not authorized; clarification is required before writing.`);
  }
}

function getAIWriteTargetPreflightFailure(
  actions: CanvasAction[],
  store: ReturnType<typeof useEditorStore.getState>,
): string | undefined {
  for (const action of actions) {
    try {
      validateAIWriteTargetPreflightAction(action, store);
    } catch (error) {
      return `${action.type}: ${getErrorMessage(error)}`;
    }
  }

  return undefined;
}

type ResponsiveBreakpointKey = 'mobile' | 'tablet' | 'desktop';

function isResponsiveBreakpointKey(value: string): value is ResponsiveBreakpointKey {
  return value === 'mobile' || value === 'tablet' || value === 'desktop';
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isComponentType(value: unknown): value is ComponentType {
  const componentTypes: readonly string[] = Object.values(ComponentType);
  return typeof value === 'string' && componentTypes.includes(value);
}

function getBreakpointStyle(value: unknown): Record<string, unknown> | undefined {
  const breakpointData = getRecord(value);
  return getRecord(breakpointData?.style);
}

function getToJSONRecord(value: unknown): Record<string, unknown> | undefined {
  const record = getRecord(value);
  const toJSON = record?.toJSON;
  if (typeof toJSON !== 'function') {
    return undefined;
  }

  const jsonValue: unknown = toJSON.call(value);
  return getRecord(jsonValue);
}

function getBreakpointOverrideValue(value: unknown, key: string): unknown {
  const breakpointData = getRecord(value);
  const style = getRecord(breakpointData?.style);
  if (style && style[key] !== undefined) {
    return style[key];
  }

  const styleSnapshot = getToJSONRecord(style);
  if (styleSnapshot?.[key] !== undefined) {
    return styleSnapshot[key];
  }

  const properties = getRecord(breakpointData?.properties);
  if (properties?.[key] !== undefined) {
    return properties[key];
  }

  const propertiesSnapshot = getToJSONRecord(properties);
  if (propertiesSnapshot?.[key] !== undefined) {
    return propertiesSnapshot[key];
  }

  try {
    const serialized = JSON.stringify(value);
    if (!serialized) {
      return undefined;
    }

    const parsed: unknown = JSON.parse(serialized);
    const parsedBreakpointData = getRecord(parsed);
    const parsedStyle = getRecord(parsedBreakpointData?.style);
    if (parsedStyle?.[key] !== undefined) {
      return parsedStyle[key];
    }

    const parsedProperties = getRecord(parsedBreakpointData?.properties);
    return parsedProperties?.[key];
  } catch {
    return undefined;
  }
}

function getComponentInput(value: unknown): ComponentInput | undefined {
  const component = getRecord(value);
  if (!component || !isComponentType(component.type)) {
    return undefined;
  }

  const safeComponent = cloneSafeOwnEnumerableRecord(component);

  return {
    ...safeComponent,
    type: component.type,
    name: getStringValue(component.name) ?? '',
    pageId: getStringValue(component.pageId) ?? '',
    properties: cloneSafeOwnEnumerableRecord(component.properties),
    style: getRecord(component.style) ? cloneSafeOwnEnumerableRecord(component.style) : undefined,
    children: getStringArray(component.children),
  } satisfies ComponentInput;
}

function getComponentUpdate(value: unknown): (Record<string, unknown> & ComponentUpdateWithBreakpoint) | undefined {
  const update = getRecord(value);
  if (!update) {
    return undefined;
  }

  return cloneSafeOwnEnumerableRecord(update);
}

function getComponentIdCandidate(id: unknown): string | undefined {
  if (typeof id === 'string' && id.length > 0) {
    return id;
  }

  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(id);
  }

  return undefined;
}

interface MoveTargetParent {
  hasTarget: boolean;
  parentId: string | undefined;
}

function getMoveTargetParentValue(value: unknown): MoveTargetParent {
  if (value === null) {
    return { hasTarget: true, parentId: undefined };
  }

  const parentId = getComponentIdCandidate(value);
  return parentId
    ? { hasTarget: true, parentId }
    : { hasTarget: false, parentId: undefined };
}

function getMoveTargetParent(payload: Record<string, unknown>): MoveTargetParent {
  if (Object.hasOwn(payload, 'parentId')) {
    return getMoveTargetParentValue(payload.parentId);
  }

  if (Object.hasOwn(payload, 'newParentId')) {
    return getMoveTargetParentValue(payload.newParentId);
  }

  return { hasTarget: false, parentId: undefined };
}

function getMoveTargetParentId(payload: Record<string, unknown>): string | undefined {
  return getMoveTargetParent(payload).parentId;
}

function getStructuralParentUpdateTarget(update: Record<string, unknown>): MoveTargetParent | undefined {
  return Object.hasOwn(update, 'parentId')
    ? getMoveTargetParentValue(update.parentId)
    : undefined;
}

function addStructuralParentUpdateTargetIds(
  targetIds: string[],
  componentId: string,
  update: Record<string, unknown>,
  store?: ReturnType<typeof useEditorStore.getState>,
  context?: PermissionPreflightContext,
): void {
  const targetParent = getStructuralParentUpdateTarget(update);
  if (!targetParent) {
    return;
  }

  if (targetParent.parentId) {
    targetIds.push(targetParent.parentId);
  }

  if (store) {
    const currentParentId = getComponentIdCandidate(
      resolvePreflightComponent(componentId, store, context)?.parentId,
    );
    if (currentParentId) {
      targetIds.push(currentParentId);
    }
  }
}

function applyComponentUpdate(
  store: ReturnType<typeof useEditorStore.getState>,
  componentId: string,
  changes: Record<string, unknown> & ComponentUpdateWithBreakpoint,
): void {
  const targetParent = getStructuralParentUpdateTarget(changes);
  const { parentId: _parentId, ...nonParentChanges } = changes;

  if (targetParent) {
    if (!targetParent.hasTarget) {
      throw new Error('update_component: invalid parentId target');
    }

    const componentBeforeMove = store.getComponent(componentId);
    if (!componentBeforeMove) {
      throw new Error(`update_component: componentId "${componentId}" not found`);
    }
    if (targetParent.parentId && !store.getComponent(targetParent.parentId)) {
      throw new Error(`update_component: target parentId "${targetParent.parentId}" not found`);
    }

    store.moveComponentToParent(componentId, targetParent.parentId);

    const componentAfterMove = store.getComponent(componentId);
    if (componentAfterMove?.parentId !== targetParent.parentId) {
      const targetDescription = targetParent.parentId ?? 'root';
      throw new Error(`update_component: unable to move "${componentId}" to "${targetDescription}"`);
    }
  }

  if (Object.keys(nonParentChanges).length > 0) {
    store.updateComponent(componentId, nonParentChanges);
  }
}

type PermissionPreflightComponent = Pick<Component, 'id' | 'name' | 'type' | 'metadata' | 'parentId'>;

interface PermissionPreflightContext {
  createdComponents: ReadonlyMap<string, PermissionPreflightComponent>;
}

function resolvePreflightComponent(
  id: string,
  store: ReturnType<typeof useEditorStore.getState>,
  context?: PermissionPreflightContext,
): PermissionPreflightComponent | undefined {
  return store.getComponent(id) ?? context?.createdComponents.get(id);
}

function getActionTargetComponentIds(
  action: CanvasAction,
  store?: ReturnType<typeof useEditorStore.getState>,
  context?: PermissionPreflightContext,
): string[] {
  const targetIds: string[] = [];
  const payload = getRecord(action.payload) ?? {};

  if (action.type === 'add_component') {
    const parentId = getComponentIdCandidate(payload.parentId);
    if (parentId) {
      targetIds.push(parentId);
    }

    const component = payload.component;
    const componentRecord = getRecord(component);
    if (componentRecord) {
      const nestedParentId = getComponentIdCandidate(componentRecord.parentId);
      if (nestedParentId) {
        targetIds.push(nestedParentId);
      }
    }

    return [...new Set(targetIds)];
  }

  if (action.type === 'move_component') {
    const componentId = getComponentIdCandidate(payload.componentId) || getComponentIdCandidate(payload.id);
    if (componentId) {
      targetIds.push(componentId);
    }

    const parentId = getMoveTargetParentId(payload);
    if (parentId) {
      targetIds.push(parentId);
    }

    const oldParentId = getComponentIdCandidate(payload.oldParentId);
    if (oldParentId) {
      targetIds.push(oldParentId);
    } else if (componentId && store) {
      const currentParentId = getComponentIdCandidate(resolvePreflightComponent(componentId, store, context)?.parentId);
      if (currentParentId) {
        targetIds.push(currentParentId);
      }
    }

    return [...new Set(targetIds)];
  }

  if (action.type === 'batch_update' && Array.isArray(payload.components)) {
    for (const componentUpdate of payload.components) {
      if (!componentUpdate || typeof componentUpdate !== 'object') {
        continue;
      }

      const update = getRecord(componentUpdate);
      if (!update) {
        continue;
      }
      const componentId = getComponentIdCandidate(update.id) || getComponentIdCandidate(update.componentId);
      if (componentId) {
        targetIds.push(componentId);
        addStructuralParentUpdateTargetIds(targetIds, componentId, update, store, context);
      }
    }

    if (targetIds.length > 0) {
      return [...new Set(targetIds)];
    }
  }

  const componentId = getComponentIdCandidate(payload.componentId) || getComponentIdCandidate(payload.id);
  if (componentId) {
    targetIds.push(componentId);
    if (action.type === 'update_component') {
      const changes = getRecord(payload.changes);
      if (changes) {
        addStructuralParentUpdateTargetIds(targetIds, componentId, changes, store, context);
      }
    }
  } else {
    const parentId = getComponentIdCandidate(payload.parentId);
    if (parentId) {
      targetIds.push(parentId);
    }
  }

  return [...new Set(targetIds)];
}

interface DeniedCanvasAction {
  category: AICommandMutationCategory;
}

function getStoreComponentsByIds(
  targetComponentIds: string[],
  store: ReturnType<typeof useEditorStore.getState>,
  context?: PermissionPreflightContext,
): PermissionPreflightComponent[] {
  return targetComponentIds
    .map((id) => resolvePreflightComponent(id, store, context))
    .filter((component): component is PermissionPreflightComponent => component !== undefined);
}

function getDeniedBatchUpdateAction(
  action: CanvasAction,
  store: ReturnType<typeof useEditorStore.getState>,
  context?: PermissionPreflightContext,
): DeniedCanvasAction | null {
  const components = action.payload?.components;
  const guard = createCurrentUserAICommandPermissionGuard(store.project);

  if (!Array.isArray(components)) {
    const categories = getMutationCategoriesForCanvasAction(action);
    const result = guard({
      categories,
      project: store.project,
      targetComponentIds: [],
      targetComponents: [],
    });

    return result.allowed
      ? null
      : { category: result.blockedCategory || categories[0] || 'content' };
  }

  let evaluatedComponentUpdate = false;
  for (const componentUpdate of components) {
    if (!componentUpdate || typeof componentUpdate !== 'object') {
      continue;
    }

    evaluatedComponentUpdate = true;
    const update = getRecord(componentUpdate);
    if (!update) {
      continue;
    }
    const categories = getMutationCategoriesForBatchComponentUpdate(update);
    const componentId = getComponentIdCandidate(update.id) || getComponentIdCandidate(update.componentId);
    const targetComponentIds = componentId ? [componentId] : [];
    if (componentId) {
      addStructuralParentUpdateTargetIds(targetComponentIds, componentId, update, store, context);
    }
    const result = guard({
      categories,
      project: store.project,
      targetComponentIds,
      targetComponents: getStoreComponentsByIds(targetComponentIds, store, context),
    });

    if (!result.allowed) {
      return {
        category: result.blockedCategory || categories[0] || 'content',
      };
    }
  }

  if (!evaluatedComponentUpdate) {
    const categories = getMutationCategoriesForCanvasAction(action);
    const result = guard({
      categories,
      project: store.project,
      targetComponentIds: [],
      targetComponents: [],
    });

    if (!result.allowed) {
      return {
        category: result.blockedCategory || categories[0] || 'content',
      };
    }
  }

  return null;
}

function getDeniedCanvasAction(
  action: CanvasAction,
  store: ReturnType<typeof useEditorStore.getState>,
  context?: PermissionPreflightContext,
): DeniedCanvasAction | null {
  if (action.type === 'batch_update') {
    return getDeniedBatchUpdateAction(action, store, context);
  }

  const categories = getMutationCategoriesForCanvasAction(action);
  if (categories.length === 0) {
    return null;
  }

  const guard = createCurrentUserAICommandPermissionGuard(store.project);
  const targetComponentIds = getActionTargetComponentIds(action, store, context);
  const targetComponents = getStoreComponentsByIds(targetComponentIds, store, context);
  const result = guard({
    categories,
    project: store.project,
    targetComponentIds,
    targetComponents,
    isCanvasMutation: action.type === 'add_page' || action.type === 'update_page' || action.type === 'replace_page',
  });

  if (result.allowed) {
    return null;
  }

  const category = result.blockedCategory || categories[0] || 'content';
  return {
    category,
  };
}

function getAddedComponentForPermissionPreflight(action: CanvasAction): PermissionPreflightComponent | undefined {
  if (action.type !== 'add_component') {
    return undefined;
  }

  const component = getComponentInput(action.payload.component);
  const componentId = getComponentIdCandidate(component?.id);
  if (!component || !componentId) {
    return undefined;
  }

  return {
    id: componentId,
    name: component.name,
    type: component.type,
    metadata: component.metadata,
    parentId: getComponentIdCandidate(action.payload.parentId) ?? component.parentId,
  };
}

function getDeniedCanvasActionsForPreflight(
  actions: CanvasAction[],
  store: ReturnType<typeof useEditorStore.getState>,
): DeniedCanvasAction[] {
  const createdComponents = new Map<string, PermissionPreflightComponent>();
  const deniedActions: DeniedCanvasAction[] = [];

  for (const action of actions) {
    const context: PermissionPreflightContext = { createdComponents };
    const deniedAction = getDeniedCanvasAction(action, store, context);
    if (deniedAction) {
      deniedActions.push(deniedAction);
    }

    const addedComponent = getAddedComponentForPermissionPreflight(action);
    if (addedComponent) {
      createdComponents.set(addedComponent.id, addedComponent);
    }
  }

  return deniedActions;
}

function formatDeniedCanvasBatchMessage(
  deniedActions: DeniedCanvasAction[],
  totalActions: number,
): string {
  const blockedCount = deniedActions.length;
  const allowedCount = Math.max(0, totalActions - blockedCount);
  const uniqueBlockedLabels = [...new Set(deniedActions.map((action) =>
    getAICommandMutationCategoryLabel(action.category)
  ))];
  const blockedLabels = uniqueBlockedLabels.join(', ');

  return [
    'Blocked by permissions before applying any actions.',
    `${blockedCount} blocked; ${allowedCount} allowed by pre-check.`,
    blockedLabels
      ? `Blocked ${uniqueBlockedLabels.length === 1 ? 'category' : 'categories'}: ${blockedLabels}.`
      : undefined,
    'No actions were applied.',
  ].filter(Boolean).join(' ');
}

function normalizeBreakpointComparableValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return String(value);

  const normalized = value.trim().toLowerCase();
  const pxMatch = normalized.match(/^(-?\d+(?:\.\d+)?)px$/);
  if (pxMatch) return pxMatch[1];
  return normalized;
}

function breakpointValuesConflict(existing: unknown, incoming: unknown): boolean {
  return normalizeBreakpointComparableValue(existing) !== normalizeBreakpointComparableValue(incoming);
}

/**
 * Applies agent-generated CanvasActions to the editor store.
 * All actions are wrapped in a single undo batch so Cmd+Z reverts the entire operation.
 */
export function applyAgentResponse(
  response: AgentVisualResponse,
  userMessage: string,
): ApplyResult {
  const store = useEditorStore.getState();
  const applied: string[] = [];
  const failed: string[] = [];

  if (!response.actions || response.actions.length === 0) {
    return { applied, failed, totalActions: 0 };
  }

  // Capture state before applying for visual diff
  const beforeSnapshot = captureStateSnapshot(store);

  const writePreflightFailure = getAIWriteTargetPreflightFailure(response.actions, store);
  if (writePreflightFailure) {
    return {
      applied,
      failed: [writePreflightFailure],
      totalActions: response.actions.length,
    };
  }

  const deniedActions = getDeniedCanvasActionsForPreflight(response.actions, store);
  if (deniedActions.length > 0) {
    const deniedMessage = formatDeniedCanvasBatchMessage(deniedActions, response.actions.length);
    return {
      applied,
      failed: [deniedMessage],
      totalActions: response.actions.length,
    };
  }

  // Reset page ID mapping for this response
  _pageIdMap.clear();

  // Start batch — all actions will be a single undo entry
  store.startUndoBatch(userMessage);

  try {
    for (const action of response.actions) {
      // Pre-apply validation: skip low-confidence actions
      const confidence = action.metadata?.confidence;
      if (typeof confidence === 'number' && confidence < 0.3) {
        console.warn('CanvasActionApplicator: skipping low-confidence action', action.type, confidence);
        failed.push(`${action.type}: confidence too low (${confidence})`);
        continue;
      }
      if (typeof confidence === 'number' && confidence < 0.6) {
        console.warn('CanvasActionApplicator: low confidence action', action.type, confidence);
      }

      // Verify componentId exists for update/delete operations
      if (action.type === 'update_component' || action.type === 'delete_component') {
        const targetId = action.payload.componentId;
        if (targetId) {
          const exists = useEditorStore.getState().getComponent(targetId) !== undefined;
          if (!exists) {
            console.warn('CanvasActionApplicator: componentId not found', action.type, targetId);
            failed.push(`${action.type}: componentId "${targetId}" not found`);
            continue;
          }
        }
      }

      try {
        applyAction(action, store);
        applied.push(action.type);
      } catch (err) {
        console.warn('CanvasActionApplicator: action failed', action.type, err);
        failed.push(`${action.type}: ${getErrorMessage(err)}`);
      }
    }
  } finally {
    // Validate structural integrity before committing the batch
    try { validateStructuralIntegrity(store); } catch (e) { console.warn('CanvasActionApplicator: structural validation error:', e); }
    // Check for template metadata drift after structural changes
    try { checkTemplateMetadataDrift(store, response.actions); } catch (_e) { /* non-critical */ }
    // Always end the batch, even if some actions failed
    store.endUndoBatch();
  }

  // Compute diff
  const afterSnapshot = captureStateSnapshot(store);
  const diff = diffSnapshots(beforeSnapshot, afterSnapshot);

  // Auto-version after agent generation (fire-and-forget)
  if (applied.length > 0) {
    autoVersionAfterAgent(userMessage, response).catch(() => {});
  }

  return { applied, failed, totalActions: response.actions.length, diff };
}

/** Create a version entry after agent generation with agent metadata */
async function autoVersionAfterAgent(userMessage: string, response: AgentVisualResponse): Promise<void> {
  logger.info(`[Phase6:CanvasAction] autoVersionAfterAgent start`, { message: userMessage.slice(0, 60), actionCount: response.actions?.length ?? 0 });
  try {
    const { useVersioningStore } = await import('@/stores/versioning/versioning-store');
    const vStore = useVersioningStore.getState();
    const editorStore = useEditorStore.getState();

    const pages = editorStore.pages || [];
    const pageComponents = new Map<string, import('@/types').Component[]>();
    for (const page of pages) {
      const comps = Object.values(editorStore.components || {}).filter(
        (c: import('@/types').Component) => c.pageId === page.id,
      );
      pageComponents.set(page.id, comps);
    }
    const snapshot = vStore.createSnapshot(pages, pageComponents);

    const versionName = `Agent: ${userMessage.slice(0, 60)}${userMessage.length > 60 ? '...' : ''}`;
    await vStore.createVersion({
      name: versionName,
      description: `Auto-versioned after agent generation`,
      snapshot,
      changes: vStore.getPendingChanges(),
      tags: ['agent-generated'],
    });
    logger.info(`[Phase6:CanvasAction] autoVersionAfterAgent version created`, { versionName });
  } catch {
    // Non-critical — version creation failure shouldn't block the user
  }
}

// --- Streaming mode ---

const ORPHAN_BATCH_TIMEOUT_MS = 180000; // 3 minutes — data-first pipelines can take 90-120s
let _streamingActive = false;
let _orphanTimer: ReturnType<typeof setTimeout> | null = null;
let _batchThreadId: string | null = null;
let _batchComponentIds: string[] = [];
let _batchPageIds: string[] = [];
let _batchAppliedActions: CanvasAction[] = [];
const _pageIdMap = new Map<string, string>(); // agent pageId → store pageId

/**
 * Begin a streaming undo batch. All subsequent `applyIncremental()` calls
 * are grouped into a single undo entry.
 */
export function startStreamingBatch(userMessage: string, threadId?: string): void {
  if (_streamingActive) {
    console.warn('CanvasActionApplicator: streaming batch already active, committing previous');
    commitStreamingBatch();
  }

  const store = useEditorStore.getState();
  _batchBeforeSnapshot = captureStateSnapshot(store);
  store.startUndoBatch(userMessage);
  _streamingActive = true;
  _batchThreadId = threadId || null;
  _batchComponentIds = [];
  _batchPageIds = [];
  _batchAppliedActions = [];
  _pageIdMap.clear();

  // Safety: auto-commit orphaned batches after 60 seconds
  _orphanTimer = setTimeout(() => {
    if (_streamingActive) {
      console.warn('CanvasActionApplicator: auto-committing orphaned streaming batch');
      commitStreamingBatch();
    }
  }, ORPHAN_BATCH_TIMEOUT_MS);
}

/**
 * Apply actions incrementally during a streaming batch.
 * Returns applied/failed results for this step only.
 */
export function applyIncremental(
  step: string,
  actions: CanvasAction[],
): { applied: string[]; failed: string[] } {
  const store = useEditorStore.getState();
  const applied: string[] = [];
  const failed: string[] = [];

  const writePreflightFailure = getAIWriteTargetPreflightFailure(actions, store);
  if (writePreflightFailure) {
    rollbackStreamingBatch();
    return {
      applied,
      failed: [writePreflightFailure],
    };
  }

  const deniedActions = getDeniedCanvasActionsForPreflight(actions, store);
  if (deniedActions.length > 0) {
    const deniedMessage = formatDeniedCanvasBatchMessage(deniedActions, actions.length);
    rollbackStreamingBatch();
    return {
      applied,
      failed: [deniedMessage],
    };
  }

  for (const action of actions) {
    // Skip low-confidence actions
    const confidence = action.metadata?.confidence;
    if (typeof confidence === 'number' && confidence < 0.3) {
      failed.push(`${action.type}: confidence too low (${confidence})`);
      continue;
    }

    // Verify target exists for update/delete
    if (action.type === 'update_component' || action.type === 'delete_component') {
      const targetId = action.payload.componentId;
      if (targetId) {
        const exists = useEditorStore.getState().getComponent(targetId) !== undefined;
        if (!exists) {
          failed.push(`${action.type}: componentId "${targetId}" not found`);
          continue;
        }
      }
    }

    try {
      applyAction(action, store);
      applied.push(action.type);
      _batchAppliedActions.push(action);

      // Track component IDs for feedback loop
      const addedComponentId = action.type === 'add_component'
        ? getComponentIdCandidate(action.payload?.component?.id)
        : undefined;
      if (addedComponentId) {
        _batchComponentIds.push(addedComponentId);
      } else if (action.type === 'update_component' && action.payload?.componentId) {
        _batchComponentIds.push(action.payload.componentId);
      }
    } catch (err) {
      failed.push(`${action.type}: ${getErrorMessage(err)}`);
    }
  }

  return { applied, failed };
}

/**
 * Commit the streaming batch — pushes the undo snapshot to history.
 * After this, Cmd+Z undoes the entire streaming generation.
 *
 * @param threadId - Optional thread ID override. Falls back to the ID set in startStreamingBatch.
 * @param componentIds - Optional component IDs override. Falls back to IDs collected during applyIncremental.
 */
export function commitStreamingBatch(threadId?: string, componentIds?: string[]): void {
  if (!_streamingActive) return;

  if (_orphanTimer) {
    clearTimeout(_orphanTimer);
    _orphanTimer = null;
  }

  const store = useEditorStore.getState();
  store.endUndoBatch();
  _streamingActive = false;

  // Check for template metadata drift after structural changes
  try { checkTemplateMetadataDrift(store, _batchAppliedActions); } catch (_e) { /* non-critical */ }

  // Compute diff from before-snapshot
  if (_batchBeforeSnapshot) {
    const afterSnapshot = captureStateSnapshot(store);
    _lastStreamingDiff = diffSnapshots(_batchBeforeSnapshot, afterSnapshot);
    _batchBeforeSnapshot = null;
  }

  // Start feedback tracking — positive signal after 30s if no undo
  const tid = threadId || _batchThreadId;
  const cids = componentIds || _batchComponentIds;
  if (tid && cids.length > 0) {
    PostActionTracker.startTracking(tid, cids);
  }

  _batchThreadId = null;
  _batchComponentIds = [];
  _batchAppliedActions = [];
  _batchPageIds = [];
}

/**
 * Rollback the streaming batch — commits then immediately undoes,
 * restoring the canvas to its pre-generation state.
 *
 * @param threadId - Optional thread ID override. Falls back to the ID set in startStreamingBatch.
 */
export function rollbackStreamingBatch(threadId?: string): void {
  if (!_streamingActive) return;

  if (_orphanTimer) {
    clearTimeout(_orphanTimer);
    _orphanTimer = null;
  }

  // Send immediate negative feedback — user aborted the generation
  const tid = threadId || _batchThreadId;
  if (tid) {
    PostActionTracker.sendImmediateNegative(tid);
  }

  const store = useEditorStore.getState();
  store.endUndoBatch();
  store.undo();
  _streamingActive = false;

  _batchThreadId = null;
  _batchComponentIds = [];
  _batchAppliedActions = [];
  _batchPageIds = [];
}

/**
 * Whether a streaming batch is currently active.
 */
export function isStreamingBatchActive(): boolean {
  return _streamingActive;
}

// --- Internal ---

function applyAction(action: CanvasAction, store: ReturnType<typeof useEditorStore.getState>): void {
  switch (action.type) {
    case 'preflight_pm_write_target': {
      validateAIWriteTargetPreflightAction(action, store);
      break;
    }

    case 'add_component': {
      const comp = getComponentInput(action.payload.component);
      if (!comp) throw new Error('add_component: missing component');
      // Always prefer payload-level parentId over component-level parentId.
      // Agents sometimes hallucinate self-referencing parentId (component.parentId = component.id)
      // or set a stale/wrong parentId inside the component object. The payload-level parentId
      // is the canonical target set by the orchestrator.
      if (action.payload.parentId) {
        comp.parentId = action.payload.parentId;
      }
      const normalizedProperties = normalizeAIDataBindingPropertiesUpdate(comp.properties, comp);
      if (normalizedProperties) {
        comp.properties = normalizedProperties;
      }

      // Clone sibling visual styling to ensure visual consistency when adding to a container
      // with existing styled children (e.g., adding a 4th card to a grid).
      // Visual styling can live in THREE locations:
      //   1. component.style (gradients, shadows)
      //   2. component.properties (backgroundColor, borderRadius, padding, border)
      //   3. responsivePosition.breakpoints.*.style (responsive overrides — often the primary source)
      // For same-type siblings, ALWAYS overwrite — the sibling's actual values are ground truth,
      // while the LLM's values are guesses (editor context strips styles from siblings).
      const parentId = comp.parentId;
      if (parentId) {
        // Properties that indicate a component has visual styling (for donor detection)
        const VISUAL_DETECTION_PROPS = [
          'backgroundColor', 'borderRadius', 'padding', 'border', 'margin', 'boxShadow',
          'color', 'fontSize', 'fontWeight', 'iconSize',
          'theme', 'activeColor', 'inactiveColor',
        ] as const;
        // Properties safe to clone from donor (excludes semantic: content, label, iconName, src, text)
        const VISUAL_CLONE_PROPS = [
          'backgroundColor', 'borderRadius', 'padding', 'border', 'margin', 'boxShadow',
          'color', 'fontSize', 'fontWeight', 'fontFamily', 'textAlign',
          'iconSize', 'opacity',
          'theme', 'activeColor', 'inactiveColor', 'activeIconColor', 'inactiveIconColor',
        ] as const;
        const parent = store.getComponent(parentId);

        // Check ALL three locations for visual styling
        const hasVisualStyling = (s: NonNullable<ReturnType<typeof store.getComponent>>) => {
          if (s.style && Object.keys(s.style).length > 0) return true;
          if (VISUAL_DETECTION_PROPS.some(p => s.properties?.[p] !== undefined && s.properties[p] !== 'transparent')) return true;
          const bps = s.responsivePosition;
          if (bps?.breakpoints) {
            for (const bpData of Object.values(bps.breakpoints)) {
              const bpStyle = getBreakpointStyle(bpData);
              if (bpStyle && VISUAL_DETECTION_PROPS.some(p => bpStyle[p] !== undefined && bpStyle[p] !== 'transparent')) return true;
            }
          }
          return false;
        };

        // --- Phase 1: Direct sibling donor search ---
        let donor: NonNullable<ReturnType<typeof store.getComponent>> | undefined;
        let isSameType = false;
        let isCousinMatch = false;
        let cousinParentId: string | null = null;
        let siblingCount = 0;

        if (parent?.children?.length) {
          const siblings = parent.children
            .map((id: string) => store.getComponent(id))
            .filter((s): s is NonNullable<typeof s> => s != null);
          siblingCount = siblings.length;

          const sameSibling = siblings.find(s => s.type === comp.type && hasVisualStyling(s));
          const anySibling = siblings.find(s => hasVisualStyling(s));
          donor = sameSibling || anySibling;
          isSameType = donor === sameSibling && sameSibling !== undefined;
        }

        // --- Phase 2: Structural cousin fallback ---
        // When no direct sibling donor found, OR only a cross-type sibling was found,
        // look at the parent's siblings' children for a same-type donor.
        // A same-type cousin is always preferred over a cross-type sibling.
        // e.g. text in "Bug Zapper Container" → find text in "Accent Lights Container"
        if ((!donor || !isSameType) && parent) {
          const grandparent = parent.parentId ? store.getComponent(parent.parentId) : undefined;
          if (grandparent?.children?.length) {
            const uncles = grandparent.children
              .map((id: string) => store.getComponent(id))
              .filter((s): s is NonNullable<typeof s> => s != null && s.id !== parentId);

            for (const uncle of uncles) {
              if (!uncle.children?.length) continue;
              const cousins = uncle.children
                .map((id: string) => store.getComponent(id))
                .filter((s): s is NonNullable<typeof s> => s != null);

              const cousinDonor = cousins.find(s => s.type === comp.type && hasVisualStyling(s));
              if (cousinDonor) {
                donor = cousinDonor;
                isSameType = true;
                isCousinMatch = true;
                cousinParentId = uncle.id;
                break;
              }
            }

            // Fallback: any cousin with visual styling (cross-type)
            // Skip container donors for non-container targets to prevent card-like
            // styling (bg, border, shadow) from being cloned onto text/chart components
            if (!donor) {
              const CONTAINER_TYPES = new Set(['flex-container', 'container', 'form-container']);
              const isTargetContainer = CONTAINER_TYPES.has(comp.type);
              for (const uncle of uncles) {
                if (!uncle.children?.length) continue;
                const cousins = uncle.children
                  .map((id: string) => store.getComponent(id))
                  .filter((s): s is NonNullable<typeof s> => s != null);

                const cousinDonor = cousins.find(s => {
                  if (!hasVisualStyling(s)) return false;
                  if (!isTargetContainer && CONTAINER_TYPES.has(s.type)) return false;
                  return true;
                });
                if (cousinDonor) {
                  donor = cousinDonor;
                  isSameType = false;
                  isCousinMatch = true;
                  cousinParentId = uncle.id;
                  break;
                }
              }
            }
          }
        }

        // Track cloning details for debug suite
        const detail: CloningDetail = {
          componentId: comp.id || 'unknown',
          componentType: comp.type,
          donorId: donor?.id || null,
          donorType: donor?.type || null,
          isSameType,
          isCousinMatch,
          cousinParentId,
          siblingCount,
          clonedStyle: [],
          clonedProperties: [],
          clonedBreakpointStyles: {},
        };

        if (donor) {
          // 1. Clone top-level style (gradients, shadows, etc.)
          if (donor.style && Object.keys(donor.style).length > 0) {
            if (isSameType || !comp.style || Object.keys(comp.style).length === 0) {
              comp.style = cloneSafeOwnEnumerableRecord(donor.style);
              detail.clonedStyle = Object.keys(donor.style);
            }
          }

          // 2. Clone visual properties (backgroundColor, borderRadius, padding, border, color, fontSize, etc.)
          if (donor.properties) {
            for (const prop of VISUAL_CLONE_PROPS) {
              const donorVal = donor.properties[prop];
              if (donorVal !== undefined && donorVal !== 'transparent') {
                if (isSameType) {
                  comp.properties[prop] = donorVal;
                  detail.clonedProperties.push(prop);
                } else {
                  const compVal = comp.properties[prop];
                  if (compVal === undefined || compVal === 'transparent') {
                    comp.properties[prop] = donorVal;
                    detail.clonedProperties.push(prop);
                  }
                }
              }
            }
          }

          // 3. Clone breakpoint styles (often the primary source of visual styling)
          const donorBps = donor.responsivePosition;
          if (donorBps?.breakpoints) {
            comp.responsivePosition ??= { strategy: 'auto', breakpoints: {} };
            comp.responsivePosition.breakpoints ??= {};

            for (const [bp, bpData] of Object.entries(donorBps.breakpoints)) {
              if (!isResponsiveBreakpointKey(bp)) continue;
              const donorBpStyle = getBreakpointStyle(bpData);
              if (!donorBpStyle) continue;

              const existingBp = getRecord(comp.responsivePosition.breakpoints[bp]) ?? {};
              const existingBpStyle = getRecord(existingBp.style) ?? {};
              const mergedStyle = cloneSafeOwnEnumerableRecord(existingBpStyle);
              const clonedInBp: string[] = [];

              for (const prop of VISUAL_CLONE_PROPS) {
                const donorVal = donorBpStyle[prop];
                if (donorVal !== undefined && donorVal !== 'transparent') {
                  if (isSameType || mergedStyle[prop] === undefined || mergedStyle[prop] === 'transparent') {
                    mergedStyle[prop] = donorVal;
                    clonedInBp.push(prop);
                  }
                }
              }

              if (clonedInBp.length > 0) {
                comp.responsivePosition.breakpoints[bp] = {
                  ...cloneSafeOwnEnumerableRecord(existingBp),
                  style: mergedStyle,
                };
                detail.clonedBreakpointStyles[bp] = clonedInBp;
              }
            }
          }

          const matchType = isCousinMatch ? 'structural cousin' : 'sibling';
          console.debug(`[CanvasActionApplicator] Cloned visual styling from ${matchType}`, donor.id, '→', comp.id, detail);
        } else {
          console.debug('[CanvasActionApplicator] No styled sibling or cousin found for', comp.id, `(${siblingCount} siblings checked)`);
        }

        _lastCloningDetails.push(detail);
      }

      // Normalize KPI card slot flex properties at creation time
      const isNewKpiSlot = comp.type === ComponentType.FLEX_CONTAINER && /kpi.*(?:card|slot)/i.test(comp.name);
      if (isNewKpiSlot) {
        const props = comp.properties;
        if (props.justifyContent !== 'center') props.justifyContent = 'center';
        if (props.alignItems !== 'center') props.alignItems = 'center';
        // Also set in style for merge-order safety
        const style = comp.style ?? {};
        style.justifyContent = 'center';
        style.alignItems = 'center';
        comp.style = style;
      }

      store.addComponent(comp);
      break;
    }

    case 'update_component': {
      const { componentId, changes: rawChanges } = action.payload;
      if (!componentId || !rawChanges) throw new Error('update_component: missing componentId or changes');
      const rawChangesRecord = getComponentUpdate(rawChanges);
      if (!rawChangesRecord) throw new Error('update_component: changes must be an object');

      // Normalize: move known button/input properties from changes root into changes.properties.
      // The Interaction Agent sometimes puts action, formDataMapping, groupId, etc. at the
      // top level of changes instead of inside changes.properties.
      const BUTTON_PROPERTY_KEYS = [
        'action', 'formDataMapping', 'groupId', 'schemaId', 'refreshTargets',
        'submitMode', 'matchFields', 'successMessage', 'errorMessage', 'closeOnSuccess',
        'target', 'required', 'validation', 'interactions',
      ];
      let changesObj: Record<string, unknown> & ComponentUpdateWithBreakpoint = rawChangesRecord;
      const misplacedKeys = BUTTON_PROPERTY_KEYS.filter(k => k in changesObj && k !== 'interactions');
      if (misplacedKeys.length > 0) {
        const existingProps = getRecord(changesObj.properties) ?? {};
        const movedProps = cloneSafeOwnEnumerableRecord(existingProps);
        for (const key of misplacedKeys) {
          movedProps[key] = changesObj[key];
        }
        changesObj = {
          ...omitRecordKeys(changesObj, misplacedKeys),
          properties: movedProps,
        };
        console.log('[CanvasActionApplicator] Normalized misplaced properties into changes.properties:', misplacedKeys, 'for', componentId);
      }
      // Also normalize interactions[] array: extract submit-form-data actions into properties
      const interactions = Array.isArray(changesObj.interactions) ? changesObj.interactions : undefined;
      if (interactions) {
        for (const interaction of interactions) {
          const interactionRecord = getRecord(interaction);
          const actions = Array.isArray(interactionRecord?.actions) ? interactionRecord.actions : [];
          if (actions.length) {
            for (const actionCandidate of actions) {
              const act = getRecord(actionCandidate);
              if (!act) continue;
              if (act.type === 'submit-form-data' || act.type === 'toggle-visibility' || act.type === 'navigate') {
                const existingProps = getRecord(changesObj.properties) ?? {};
                const mergedProps = cloneSafeOwnEnumerableRecord(existingProps);
                // Copy relevant fields from the interaction action into properties
                if (act.type) mergedProps.action = act.type;
                for (const k of BUTTON_PROPERTY_KEYS) {
                  if (k in act && k !== 'action') mergedProps[k] = act[k];
                }
                // For toggle-visibility, also set data.targetIds from targetComponentId
                if (act.type === 'toggle-visibility' && act.targetComponentId) {
                  mergedProps.data = mergeSafeOwnEnumerableRecords(mergedProps.data, {
                    targetIds: [act.targetComponentId],
                  });
                }
                changesObj.properties = mergedProps;
              }
            }
          }
        }
        changesObj = omitRecordKeys(changesObj, ['interactions']);
      }
      const targetComponent = store.getComponent(componentId);
      const normalizedProperties = normalizeAIDataBindingPropertiesUpdate(changesObj.properties, targetComponent);
      if (normalizedProperties) {
        changesObj = {
          ...changesObj,
          properties: normalizedProperties,
        };
      }

      let changes = changesObj;

      if (targetComponent && changes.style && typeof changes.style === 'object') {
        const styleWriteTarget = isColorSchemeWriteTarget(changes.styleWriteTarget)
          ? changes.styleWriteTarget
          : store.styleWriteTarget;
        const updatedStyle = applySchemaAwareStyleUpdate(
          targetComponent,
          changes.style,
          {
            projectSchemaVersion: store.project?.schemaVersion,
            breakpoint: action.payload.breakpoint ?? changes.breakpoint,
            styleWriteTarget,
          },
        );
        changes = {
          ...omitRecordKeys(changes, ['styleWriteTarget']),
          style: updatedStyle,
        };
      }

      // Warn when base property changes may conflict with breakpoint overrides
      {
        const component = targetComponent;
        const bps = component?.responsivePosition?.breakpoints;
        const props = getRecord(changes.properties);
        if (bps && props) {
          for (const [key, value] of Object.entries(props)) {
            for (const [bp, bpData] of Object.entries(bps)) {
              const breakpointOverride = getBreakpointOverrideValue(bpData, key);
              if (breakpointOverride !== undefined) {
                if (!breakpointValuesConflict(breakpointOverride, value)) continue;
                console.warn(
                  `CanvasActionApplicator: breakpoint "${bp}" has override for "${key}" ` +
                  `(${breakpointOverride}) that may conflict with new base value (${value}). ` +
                  `Consider using responsivePosition.breakpoints to scope the change.`
                );
              }
            }
          }
        }
      }

      // Normalize KPI card slot flex properties to prevent layer separation.
      // The Styles Agent sometimes sets justifyContent: "space-between" or omits alignment,
      // causing the icon to get pushed to the bottom of the card with empty space.
      {
        const component = store.getComponent(componentId);
        const isKpiSlot = component?.type === 'flex-container' && /kpi.*(?:card|slot)/i.test(component.name || '');
        if (isKpiSlot) {
          const styleObj = getRecord(changes.style) ?? {};
          const propsObj = getRecord(changes.properties) ?? {};
          // Force center alignment in style (highest priority in merge order)
          if (styleObj.justifyContent && styleObj.justifyContent !== 'center') {
            console.log(`[CanvasActionApplicator] KPI slot ${componentId}: overriding justifyContent "${styleObj.justifyContent}" → "center"`);
            styleObj.justifyContent = 'center';
          }
          if (styleObj.alignItems && styleObj.alignItems !== 'center') {
            console.log(`[CanvasActionApplicator] KPI slot ${componentId}: overriding alignItems "${styleObj.alignItems}" → "center"`);
            styleObj.alignItems = 'center';
          }
          // Also fix in properties (Layout Agent level)
          if (propsObj.justifyContent && propsObj.justifyContent !== 'center') {
            propsObj.justifyContent = 'center';
          }
          if (propsObj.alignItems && propsObj.alignItems !== 'center') {
            propsObj.alignItems = 'center';
          }
        }
      }

      // Reconcile submit-form-data schema/group IDs with actual data components on the page.
      // The Interaction Agent may use schema IDs from the data wizard, but the DC Agent's queries
      // use different schema IDs from Data Discovery. This ensures submit saves to the same
      // collection that queries read from.
      {
        const props = getRecord(changes.properties);
        if (props?.action === 'submit-form-data' && (props.schemaId || props.groupId)) {
          const buttonGroupId = getStringValue(props.groupId);
          const buttonSchemaId = getStringValue(props.schemaId);
          // Collect schema/group IDs actually in use by data-bound components
          const dataSchemaMap = new Map<string, { schemaId: string; groupId: string; fields: Set<string> }>();
          for (const comp of store.components) {
            const ds = getRecord(comp.properties.dataSource);
            const qd = getRecord(ds?.queryDefinition);
            const sid = getStringValue(qd?.schemaId);
            const groupId = getStringValue(qd?.groupId);
            if (sid && groupId) {
              let entry = dataSchemaMap.get(sid);
              if (!entry) {
                entry = { schemaId: sid, groupId, fields: new Set() };
                dataSchemaMap.set(sid, entry);
              }
              const measures = Array.isArray(qd?.measures) ? qd.measures : undefined;
              if (measures) {
                for (const m of measures) {
                  const field = getStringValue(getRecord(m)?.field);
                  if (field) entry.fields.add(field);
                }
              }
              // Also collect groupBy fields
              const groupBy = Array.isArray(qd?.groupBy) ? qd.groupBy : undefined;
              if (groupBy) {
                for (const g of groupBy) {
                  const field = getStringValue(getRecord(g)?.field);
                  if (field) entry.fields.add(field);
                }
              }
            }
          }

          if (dataSchemaMap.size > 0) {
            // Check if button's groupId matches any data component
            const dataGroupIds = new Set([...dataSchemaMap.values()].map(d => d.groupId));
            if (buttonGroupId && !dataGroupIds.has(buttonGroupId)) {
              // GroupId mismatch — find best matching schema by form field overlap
              const formDataMapping = Array.isArray(props.formDataMapping) ? props.formDataMapping : [];
              const formFields = formDataMapping
                .map((mapping) => {
                  const mappingRecord = getRecord(mapping);
                  return getStringValue(mappingRecord?.schemaField) ?? getStringValue(mappingRecord?.field);
                })
                .filter((field): field is string => field !== undefined);

              // Tokenize field names for fuzzy matching (e.g. "goalWeight" → {goal, weight})
              const tokenize = (name: string) => {
                const n = name.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
                return new Set(n.split(/[^a-z0-9]+/).filter(Boolean));
              };
              const formTokenSets = formFields.map(f => tokenize(f));

              let bestMatch: { schemaId: string; groupId: string } | null = null;
              let bestScore = 0;
              for (const [, info] of dataSchemaMap) {
                // Exact field overlap (weighted 2x)
                const exactOverlap = formFields.filter(f => info.fields.has(f)).length;
                // Tokenized overlap for fuzzy matching
                const schemaTokenSets = [...info.fields].map(f => tokenize(f));
                let tokenScore = 0;
                for (const formTokens of formTokenSets) {
                  let bestFieldScore = 0;
                  for (const schemaTokens of schemaTokenSets) {
                    const overlap = [...formTokens].filter(t => schemaTokens.has(t)).length;
                    const score = overlap / Math.max(formTokens.size, schemaTokens.size);
                    if (score > bestFieldScore) bestFieldScore = score;
                  }
                  tokenScore += bestFieldScore;
                }
                const score = exactOverlap * 2 + tokenScore;
                if (score > bestScore) {
                  bestScore = score;
                  bestMatch = { schemaId: info.schemaId, groupId: info.groupId };
                }
              }

              // Fallback: single schema or closest field count
              if (!bestMatch && dataSchemaMap.size === 1) {
                const only = [...dataSchemaMap.values()][0];
                bestMatch = { schemaId: only.schemaId, groupId: only.groupId };
              }
              if (!bestMatch && dataSchemaMap.size > 1) {
                let closestDiff = Infinity;
                for (const [, info] of dataSchemaMap) {
                  const diff = Math.abs(info.fields.size - formFields.length);
                  if (diff < closestDiff) {
                    closestDiff = diff;
                    bestMatch = { schemaId: info.schemaId, groupId: info.groupId };
                  }
                }
              }

              if (bestMatch) {
                console.log(`[CanvasActionApplicator] Reconciled submit schema/group for ${componentId}: schema ${buttonSchemaId ?? ''} → ${bestMatch.schemaId}, group ${buttonGroupId} → ${bestMatch.groupId}`);
                props.schemaId = bestMatch.schemaId;
                props.groupId = bestMatch.groupId;
              }
            }
          }
        }
      }

      applyComponentUpdate(store, componentId, changes);
      break;
    }

    case 'delete_component': {
      const { componentId } = action.payload;
      if (!componentId) throw new Error('delete_component: missing componentId');
      store.deleteComponent(componentId);
      break;
    }

    case 'reorder_children': {
      const { componentId, newOrder } = action.payload;
      if (!componentId || !newOrder) throw new Error('reorder_children: missing componentId or newOrder');
      store.reorderChildrenInContainer(componentId, newOrder);
      break;
    }

    case 'batch_update': {
      const { components } = action.payload;
      if (!components || !Array.isArray(components)) throw new Error('batch_update: missing components array');
      const componentUpdates: Array<Record<string, unknown> & ComponentUpdateWithBreakpoint> = [];
      for (const comp of components) {
        const componentUpdate = getComponentUpdate(comp);
        if (!componentUpdate) {
          continue;
        }

        const id = getComponentIdCandidate(componentUpdate.id) || getComponentIdCandidate(componentUpdate.componentId);
        const targetComponent = id ? store.getComponent(id) : undefined;
        const normalizedProperties = normalizeAIDataBindingPropertiesUpdate(
          componentUpdate.properties,
          targetComponent,
        );
        componentUpdates.push(normalizedProperties
          ? {
              ...componentUpdate,
              properties: normalizedProperties,
            }
          : componentUpdate);
      }

      for (const componentUpdate of componentUpdates) {
        const id = getComponentIdCandidate(componentUpdate.id) || getComponentIdCandidate(componentUpdate.componentId);
        if (id) {
          applyComponentUpdate(store, id, componentUpdate);
        }
      }
      break;
    }

    case 'add_page': {
      const { pageId, pageName, pageDescription, pageOrder } = action.payload;
      const newPage = store.addPage({
        name: pageName || 'New Page',
        description: pageDescription,
        template: PageTemplate.BLANK,
        order: pageOrder,
      });
      // Map agent's page ID to the store-assigned ID
      if (pageId) {
        _pageIdMap.set(pageId, newPage.id);
      }
      _batchPageIds.push(newPage.id);
      break;
    }

    case 'switch_page': {
      const targetPageId = action.payload.pageId;
      if (!targetPageId) throw new Error('switch_page: missing pageId');
      const storePageId = _pageIdMap.get(targetPageId) || targetPageId;
      store.switchToPage(storePageId);
      break;
    }

    case 'link_pages': {
      // Navigation linking: create navigation components on each page
      const { targetPageIds, navigationConfig } = action.payload;
      if (!targetPageIds || targetPageIds.length < 2) break;
      const remappedIds = targetPageIds.map(id => _pageIdMap.get(id) || id);
      console.info('CanvasActionApplicator: link_pages', {
        pageCount: remappedIds.length,
        navType: getRecord(navigationConfig)?.type,
      });
      // Navigation component creation is handled by the editor's page system
      break;
    }

    case 'create_branch': {
      const { branchName, branchDescription } = action.payload;
      if (!branchName) break;
      logger.info(`[Phase6:CanvasAction] Creating branch`, { branchName });
      // Import versioning store dynamically to avoid circular deps
      import('@/stores/versioning/versioning-store').then(({ useVersioningStore }) => {
        const vStore = useVersioningStore.getState();
        vStore.createBranch({
          name: branchName,
          description: branchDescription,
          baseVersion: vStore.currentVersion || 0,
        }).catch(err => console.warn('create_branch failed:', err));
      });
      break;
    }

    case 'move_component': {
      const { componentId, oldParentId, position } = action.payload;
      const targetParent = getMoveTargetParent(action.payload);
      if (!componentId || !targetParent.hasTarget) {
        throw new Error('move_component: missing componentId or target parentId');
      }

      const componentBeforeMove = store.getComponent(componentId);
      if (!componentBeforeMove) {
        throw new Error(`move_component: componentId "${componentId}" not found`);
      }
      if (targetParent.parentId && !store.getComponent(targetParent.parentId)) {
        throw new Error(`move_component: target parentId "${targetParent.parentId}" not found`);
      }

      store.moveComponentToParent(componentId, targetParent.parentId, position);

      const componentAfterMove = store.getComponent(componentId);
      if (componentAfterMove?.parentId !== targetParent.parentId) {
        const targetDescription = targetParent.parentId ?? 'root';
        throw new Error(`move_component: unable to move "${componentId}" to "${targetDescription}"`);
      }
      if (oldParentId && oldParentId !== componentBeforeMove.parentId) {
        console.warn('CanvasActionApplicator: move_component oldParentId did not match current parent', {
          componentId,
          oldParentId,
          currentParentId: componentBeforeMove.parentId,
        });
      }
      break;
    }

    case 'update_page':
    case 'replace_page':
      console.warn(`CanvasActionApplicator: ${action.type} not yet fully implemented`);
      break;

    default:
      console.warn('CanvasActionApplicator: unknown action type', action.type);
  }
}

/**
 * After agent actions modify a template, check if metadata needs updating.
 * Logs actual component state when structural actions (add/delete/move) are applied.
 * Informational only — the actual template-library.json update happens via
 * scripts/updateTemplateMetadata.ts which should be run periodically.
 */
function checkTemplateMetadataDrift(
  store: ReturnType<typeof useEditorStore.getState>,
  appliedActions: CanvasAction[],
): void {
  const structuralTypes = ['add_component', 'delete_component', 'move_component'];
  const structuralActions = appliedActions.filter(a => structuralTypes.includes(a.type));
  if (structuralActions.length === 0) return;

  const components = store.components || [];
  const actualCount = components.length;
  const actualTypes = [...new Set(components.map(c => c.type))].sort();

  console.info(
    '[CanvasActionApplicator] Template structure modified.',
    `Components: ${actualCount}, Types: [${actualTypes.join(', ')}],`,
    `Structural actions: ${structuralActions.length}`,
  );
}

/**
 * Post-application structural validation.
 * Warns on inconsistencies but never blocks rendering.
 */
function validateStructuralIntegrity(store: ReturnType<typeof useEditorStore.getState>): void {
  const components = store.components;
  if (!components || components.length === 0) return;

  const componentIds = new Set(components.map(c => c.id));
  const warnings: string[] = [];

  for (const comp of components) {
    // Check parentId references an existing component
    if (comp.parentId && !componentIds.has(comp.parentId)) {
      warnings.push(
        `Component "${comp.id}" (${comp.name || comp.type}) has parentId "${comp.parentId}" which does not exist`
      );
    }

    // Check children array references exist
    if (comp.children && Array.isArray(comp.children)) {
      for (const childId of comp.children) {
        if (!componentIds.has(childId)) {
          warnings.push(
            `Component "${comp.id}" (${comp.name || comp.type}) has child "${childId}" which does not exist`
          );
        }
      }
    }

    // Check orphans: component has parentId but parent's children doesn't include it
    if (comp.parentId && componentIds.has(comp.parentId)) {
      const parent = components.find(c => c.id === comp.parentId);
      if (parent && parent.children && !parent.children.includes(comp.id)) {
        warnings.push(
          `Component "${comp.id}" has parentId "${comp.parentId}" but parent's children array doesn't include it (orphan)`
        );
      }
    }
  }

  if (warnings.length > 0) {
    console.warn('CanvasActionApplicator: structural validation warnings:', warnings);
  }
}

// --- Snapshot & Diff ---

let _batchBeforeSnapshot: Map<string, ComponentSnapshot> | null = null;
let _lastStreamingDiff: SnapshotDiff | null = null;

/**
 * Capture a lightweight snapshot of the current canvas state for diffing.
 */
export function captureStateSnapshot(
  store: ReturnType<typeof useEditorStore.getState>,
): Map<string, ComponentSnapshot> {
  const snapshot = new Map<string, ComponentSnapshot>();
  const components = store.components || [];
  for (const comp of components) {
    snapshot.set(comp.id, {
      type: comp.type || '',
      name: comp.name || '',
      parentId: comp.parentId || '',
      style: comp.style ? { ...comp.style } : {},
      properties: comp.properties ? { ...comp.properties } : {},
      size: {
        width: typeof comp.responsivePosition?.responsiveSize?.width === 'number'
          ? comp.responsivePosition.responsiveSize.width : 0,
        height: typeof comp.responsivePosition?.responsiveSize?.height === 'number'
          ? comp.responsivePosition.responsiveSize.height : 0,
      },
    });
  }
  return snapshot;
}

/**
 * Compare two state snapshots and return a human-readable diff.
 */
export function diffSnapshots(
  before: Map<string, ComponentSnapshot>,
  after: Map<string, ComponentSnapshot>,
): SnapshotDiff {
  const added: string[] = [];
  const removed: string[] = [];
  const modified: Array<{ componentId: string; componentName: string; componentType: string; changes: string[] }> = [];

  for (const [id, afterComp] of after) {
    const beforeComp = before.get(id);
    if (!beforeComp) {
      added.push(id);
      continue;
    }
    const changes: string[] = [];
    if (beforeComp.parentId !== afterComp.parentId) changes.push('moved to different parent');
    if (beforeComp.size.width !== afterComp.size.width || beforeComp.size.height !== afterComp.size.height) {
      changes.push(`resized (${beforeComp.size.width}\u00D7${beforeComp.size.height} \u2192 ${afterComp.size.width}\u00D7${afterComp.size.height})`);
    }
    const styleChanges = diffObjects(beforeComp.style, afterComp.style);
    if (styleChanges.length > 0) changes.push(`style: ${styleChanges.join(', ')}`);
    const propChanges = diffObjects(beforeComp.properties, afterComp.properties);
    if (propChanges.length > 0) changes.push(`properties: ${propChanges.join(', ')}`);

    if (changes.length > 0) {
      modified.push({ componentId: id, componentName: afterComp.name, componentType: afterComp.type, changes });
    }
  }

  for (const id of before.keys()) {
    if (!after.has(id)) removed.push(id);
  }

  const parts: string[] = [];
  if (added.length > 0) parts.push(`Added ${added.length} component(s)`);
  if (modified.length > 0) parts.push(`Modified ${modified.length} component(s)`);
  if (removed.length > 0) parts.push(`Removed ${removed.length} component(s)`);

  return {
    added,
    removed,
    modified,
    summary: parts.length > 0 ? parts.join(', ') : 'No changes detected',
  };
}

function diffObjects(a: Record<string, unknown>, b: Record<string, unknown>): string[] {
  const changed: string[] = [];
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of allKeys) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
      changed.push(key);
    }
  }
  return changed;
}

/**
 * Get the diff from the last committed streaming batch.
 */
export function getLastStreamingDiff(): SnapshotDiff | null {
  const diff = _lastStreamingDiff;
  _lastStreamingDiff = null;
  return diff;
}

/**
 * Undo the last property change (or globally the last agent batch).
 * Delegates to the editor store's standard undo mechanism.
 * True per-property granular undo requires sub-batch tracking not yet implemented.
 */
export function undoLastPropertyChange(_componentId?: string): boolean {
  const store = useEditorStore.getState();
  store.undo();
  return true;
}
